import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  const lastUpdated = 'August 18, 2026';

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '48px 24px', color: '#1e293b', lineHeight: '1.7' }}>
      <h1 style={{ fontSize: '32px', fontWeight: '800', color: '#1e3a5f', marginBottom: '6px' }}>
        Privacy Policy
      </h1>
      <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '32px' }}>
        Last updated: {lastUpdated}
      </p>

      <p style={{ marginBottom: '24px' }}>
        ChatTech24 ("we", "us", "our") is operated by Vedvika Technologies, a brand of Dynamic
        Futuretech Private Limited ("Company"). This Privacy Policy explains how we collect, use,
        store, and protect information when you use our website (chattech24.com) and our chatbot,
        automation, and messaging platform (the "Service"), including features that connect to the
        WhatsApp Business Platform provided by Meta Platforms, Inc. ("Meta").
      </p>
      <p style={{ marginBottom: '32px' }}>
        By using the Service, you agree to the collection and use of information in accordance with
        this policy. If you do not agree, please do not use the Service.
      </p>

      <Section title="1. Information We Collect">
        <SubHeading>a. Account &amp; Business Information</SubHeading>
        <p>
          When you register for ChatTech24, we collect your name, email address, phone number,
          business/organization name, and billing details necessary to provide the Service.
        </p>

        <SubHeading>b. WhatsApp Business Platform Data</SubHeading>
        <p>
          When you connect your WhatsApp Business Account (WABA) to ChatTech24 — either manually or
          through Meta's Embedded Signup — we access and process data made available to us via the
          WhatsApp Business Platform API, including:
        </p>
        <ul style={{ paddingLeft: '22px', marginTop: '8px' }}>
          <li>Your WhatsApp Business Account ID, phone number ID, and business phone number</li>
          <li>Message content, media, and metadata sent and received through your connected number</li>
          <li>Message delivery/read status and conversation analytics</li>
          <li>Contact information of the end-users who message your WhatsApp number</li>
        </ul>
        <p style={{ marginTop: '8px' }}>
          This data is used solely to operate features you configure — such as chatbot flows,
          automated responses, AI-assisted replies, bulk campaigns, and scheduled messaging — on
          your behalf and under your instructions.
        </p>

        <SubHeading>c. Technical &amp; Usage Data</SubHeading>
        <p>
          We automatically collect log data such as IP address, browser type, device information,
          pages visited, and timestamps to maintain security and improve the Service.
        </p>

        <SubHeading>d. Payment Data</SubHeading>
        <p>
          Subscription payments are processed through third-party payment gateways (e.g., Razorpay).
          We do not store your full card or bank details on our servers; payment processors handle
          this in accordance with their own security standards.
        </p>
      </Section>

      <Section title="2. How We Use Your Information">
        <ul style={{ paddingLeft: '22px' }}>
          <li>To provide, operate, and maintain the ChatTech24 platform</li>
          <li>To send, receive, and automate WhatsApp messages on your behalf, as configured by you</li>
          <li>To process subscriptions, generate invoices, and manage billing</li>
          <li>To provide customer support and respond to inquiries</li>
          <li>To monitor usage, detect abuse, and maintain platform security</li>
          <li>To comply with legal obligations and Meta/WhatsApp Business Platform policies</li>
        </ul>
        <p style={{ marginTop: '10px' }}>
          We do not use WhatsApp message content for advertising purposes, and we do not sell your
          data or your end-users' data to third parties.
        </p>
      </Section>

      <Section title="3. Sharing of Information">
        <p>We share information only in the following circumstances:</p>
        <ul style={{ paddingLeft: '22px', marginTop: '8px' }}>
          <li>
            <strong>With Meta Platforms, Inc.</strong> — because the Service operates on top of the
            WhatsApp Business Platform, relevant data is necessarily processed by Meta in accordance
            with Meta's own Privacy Policy and WhatsApp Business Data Processing Terms.
          </li>
          <li>
            <strong>With service providers</strong> — such as our hosting, database, payment
            gateway, and AI-model providers (e.g., OpenAI), strictly to operate the features you use.
          </li>
          <li>
            <strong>For legal reasons</strong> — if required by law, regulation, or valid legal
            process, or to protect the rights, safety, and security of ChatTech24, our users, or the
            public.
          </li>
        </ul>
      </Section>

      <Section title="4. Data Retention">
        <p>
          We retain account and message data for as long as your account remains active, or as
          needed to provide the Service, comply with legal obligations, resolve disputes, and enforce
          our agreements. You may request deletion of your data as described in Section 7.
        </p>
      </Section>

      <Section title="5. Data Security">
        <p>
          We implement industry-standard technical and organizational measures — including
          encryption of stored message content, access controls, and secure infrastructure — to
          protect your information against unauthorized access, alteration, disclosure, or
          destruction. However, no method of transmission or storage is 100% secure.
        </p>
      </Section>

      <Section title="6. Cookies">
        <p>
          Our website may use cookies and similar technologies to keep you logged in, remember
          preferences, and understand how the Service is used. You can control cookies through your
          browser settings.
        </p>
      </Section>

      <Section title="7. Your Rights">
        <p>You may, subject to applicable law, request to:</p>
        <ul style={{ paddingLeft: '22px', marginTop: '8px' }}>
          <li>Access the personal data we hold about you</li>
          <li>Correct inaccurate or incomplete data</li>
          <li>Request deletion of your account and associated data</li>
          <li>Withdraw consent for optional data processing, where applicable</li>
        </ul>
        <p style={{ marginTop: '10px' }}>
          To exercise these rights, contact us at <strong>support@chattech24.com</strong>.
        </p>
      </Section>

      <Section title="8. Children's Privacy">
        <p>
          The Service is intended for business use by individuals aged 18 and above. We do not
          knowingly collect personal information from children.
        </p>
      </Section>

      <Section title="9. Third-Party Links &amp; Services">
        <p>
          Our Service may integrate with third-party platforms (e.g., Meta/WhatsApp, OpenAI,
          payment gateways). Their use of your data is governed by their own privacy policies, and we
          encourage you to review them.
        </p>
      </Section>

      <Section title="10. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be notified via
          the Service or email. Continued use of the Service after changes take effect constitutes
          acceptance of the revised policy.
        </p>
      </Section>

      <Section title="11. Contact Us">
        <p>
          If you have questions about this Privacy Policy or how your data is handled, contact us at:
        </p>
        <p style={{ marginTop: '8px' }}>
          <strong>Vedvika Technologies</strong> (Dynamic Futuretech Private Limited)
          <br />
          Email: support@chattech24.com
          <br />
          Website: chattech24.com
        </p>
      </Section>

      <p style={{ marginTop: '40px', fontSize: '13px', color: '#94a3b8' }}>
        See also our{' '}
        <Link to="/terms" style={{ color: '#1e3a5f', fontWeight: '600' }}>
          Terms and Conditions
        </Link>
        .
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '28px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e3a5f', marginBottom: '10px' }}>
        {title}
      </h2>
      <div style={{ fontSize: '15px', color: '#334155' }}>{children}</div>
    </div>
  );
}

function SubHeading({ children }) {
  return (
    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#334155', margin: '14px 0 6px' }}>
      {children}
    </h3>
  );
}

export default PrivacyPolicy;